import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import config from "../../config";
import { encryptData } from "../../utils/cryptoUtils";

/* ------------------------------------------------------------------
   Palette (same as Hypothecation buttons: Register / Check Status / Remove)
------------------------------------------------------------------- */
const COLORS = {
  info: "#0dcaf0", // Register button
  success: "#198754", // Check Status button
  danger: "#dc3545", // Remove button
  tint: "#e0f6fa", // light cyan section background
  border: "#cfe8ee",
  label: "#5b6b73",
  text: "#212529",
};

/* ------------------------------------------------------------------
   Status comes from the backend (status_key):
   success | failed | aborted | pending
------------------------------------------------------------------- */
const STATE_META = {
  success: { label: "Success", color: COLORS.success, icon: "✓" },
  failed: { label: "Failed", color: COLORS.danger, icon: "✕" },
  aborted: { label: "Aborted", color: "#fd7e14", icon: "!" },
  pending: { label: "Pending", color: "#6c757d", icon: "…" },
};

const getMandateState = (item) =>
  STATE_META[item.status_key] ? item.status_key : "pending";

/* ------------------------------------------------------------------
   Helpers
------------------------------------------------------------------- */
// NPCI mandate codes
const FREQUENCY = {
  ADHO: "As & when presented",
  DAIL: "Daily",
  WEEK: "Weekly",
  BIMN: "Bi-monthly",
  MNTH: "Monthly",
  QURT: "Quarterly",
  MIAN: "Half-yearly",
  YEAR: "Yearly",
};
const AMOUNT_TYPE = { M: "Maximum", F: "Fixed" };
const VIA = { ViaLink: "Customer Link", ViaLms: "LMS" };

const has = (v) =>
  v !== undefined && v !== null && String(v).trim() !== "";

const NA = "";

// empty / "-" values are shown as N/A
const show = (v) => (has(v) && String(v).trim() !== "-" ? v : NA);

// keys you never want to see in the cards (add the raw key name, e.g. "itc")
const HIDE_KEYS = new Set([]);

const maskLast4 = (v) => {
  const s = String(v);
  return s.length > 4 ? "X".repeat(s.length - 4) + s.slice(-4) : s;
};

const rupees = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? `₹${n.toLocaleString("en-IN")}` : String(v);
};

// "aadharNo" -> "Aadhar No", "helpdesk_email" -> "Helpdesk Email", "UMRNNumber" -> "UMRN Number"
const humanize = (key) =>
  String(key)
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b([a-z])/g, (c) => c.toUpperCase());

// mandate keys that already have a dedicated label below.
// Every OTHER key that arrives in the API response is shown automatically.
const KNOWN_MANDATE_KEYS = new Set([
  "UMRNNumber",
  "IFSCCode",
  "ifsc_code",
  "amount_type",
  "amount",
  "frequency",
  "account_number",
  "expiry_date",
  "schedule_date",
  "accountHolderName",
  "accountType",
  "aadharNo",
]);

const Chips = ({ items }) => (
  <div className="enach-handles">
    {items.map((h, i) => (
      <span key={`${h}-${i}`} className="enach-chip">
        {h}
      </span>
    ))}
  </div>
);

/* ------------------------------------------------------------------
   Build the list of fields shown in one eNach card.
   - Every key that comes in the response is shown.
   - Empty values are shown as N/A (nothing is hidden).
   - Any new key sent by the gateway is picked up automatically.
------------------------------------------------------------------- */
const buildFields = (item, state) => {
  const m = item.mandate || {};
  const hasKey = (k) => Object.prototype.hasOwnProperty.call(m, k);
  const hasMandate = Object.keys(m).length > 0;
  const f = [];

  const add = (label, value, opts = {}) => f.push({ label, value, ...opts });
  // always shown, N/A when empty
  const put = (label, value, opts) => add(label, show(value), opts);
  // mandate key: shown when the gateway sent that key (N/A when it is empty)
  const mk = (label, key, fmt) => {
    if (!hasKey(key)) return;
    add(label, has(m[key]) ? (fmt ? fmt(m[key]) : m[key]) : NA);
  };

  if (state === "success") {
    put("UMRN Number", m.UMRNNumber);
    put("e-NACH Set On", item.created_at);
    put(
      "First Debit Date",
      has(m.schedule_date) ? m.schedule_date : item.proposed_debit_date
    );
    put("Expiry Date", m.expiry_date);
  } else {
    put("Attempted On", item.created_at);
  }

  put("e-NACH Done By", item.enach_done_by);
  put("No. of Vehicles", item.no_of_vehicles);
  put(
    "Proposed Amount",
    Number(item.proposed_amount) > 0 ? rupees(item.proposed_amount) : ""
  );

  // ---- mandate details (whatever the gateway returned) ----
  if (hasKey("amount") || state === "success") {
    const amount = has(m.amount) ? m.amount : state === "success" ? item.amount : "";
    put(
      AMOUNT_TYPE[m.amount_type]
        ? `${AMOUNT_TYPE[m.amount_type]} Amount`
        : "Mandate Amount",
      has(amount) ? rupees(amount) : ""
    );
  }
  mk("Frequency", "frequency", (v) => FREQUENCY[v] || v);
  mk("Account Holder", "accountHolderName");
  mk("Account Type", "accountType");
  mk("Account Number", "account_number");
  if (hasKey("IFSCCode") || hasKey("ifsc_code")) {
    put("IFSC Code", has(m.IFSCCode) ? m.IFSCCode : m.ifsc_code);
  }
  mk("Aadhaar", "aadharNo", maskLast4);
  if (hasMandate || has(item.mandate_mobile)) {
    put("Mobile (in mandate)", item.mandate_mobile);
  }

  // every other mandate key -> auto
  Object.keys(m).forEach((k) => {
    if (!KNOWN_MANDATE_KEYS.has(k) && !HIDE_KEYS.has(k)) put(humanize(k), m[k]);
  });

  // other {key:value} groups of the gateway block (mob is already shown above)
  Object.keys(item.extra || {}).forEach((k) => {
    if (k !== "mob" && !HIDE_KEYS.has(k)) put(humanize(k), item.extra[k]);
  });

  // other top-level keys of the api_response JSON -> auto
  Object.keys(item.raw_extra || {}).forEach((k) => {
    if (!HIDE_KEYS.has(k)) put(humanize(k), item.raw_extra[k]);
  });

  put("Last Updated", has(item.response_date) ? item.response_date : item.created_at);
  put("Reference No.", item.reference_no);
  put(
    "Registered Via",
    has(item.verifed_side)
      ? VIA[item.verifed_side] || humanize(item.verifed_side)
      : ""
  );
  put("IP Address", item.ip_address);

  // every other value of the gateway message -> auto
  const ids = Array.isArray(item.gateway_ids) ? item.gateway_ids : [];
  add("Gateway IDs", ids.length > 0 ? ids.join(", ") : NA, { wide: true });

  // UPI handles
  const handles = Array.isArray(item.upi_handles) ? item.upi_handles : [];
  add("UPI Handles", handles.length > 0 ? <Chips items={handles} /> : NA, {
    wide: true,
  });

  return f;
};

const Field = ({ label, children, wide, full }) => (
  <div
    className={`enach-field ${wide ? "enach-field-wide" : ""} ${
      full ? "enach-field-full" : ""
    }`}
  >
    <div className="enach-label">{label}</div>
    <div className="enach-value">{children}</div>
  </div>
);

/* ------------------------------------------------------------------
   Customer profile (Profile Advance API) – lead level
   Everything the API returns is shown automatically.
------------------------------------------------------------------- */
const PROFILE_LABELS = {
  full_name: "Full Name",
  date_of_birth: "Date of Birth",
};

const ProfileCard = ({ profile }) => {
  const [open, setOpen] = useState(true);

  const personal = profile.personal || {};
  const documents = profile.documents || {};
  const others = profile.others || {};
  const addresses = Array.isArray(profile.addresses) ? profile.addresses : [];
  const phones = Array.isArray(profile.alternate_phones)
    ? profile.alternate_phones
    : [];
  const emails = Array.isArray(profile.emails) ? profile.emails : [];

  return (
    <div className="enach-card" style={{ borderLeftColor: COLORS.info }}>
      <div className="enach-head">
        <h5 className="enach-title">Customer Profile</h5>

        <div className="enach-actions">
          {profile.fetched_at && (
            <span className="enach-msg" style={{ color: COLORS.label }}>
              Fetched on {profile.fetched_at}
            </span>
          )}
          <button
            type="button"
            className="enach-toggle"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? "Hide" : "Show"}
          </button>
        </div>
      </div>

      {open && (
        <>
          <div className="enach-body">
            {Object.keys(personal).map((k) => (
              <Field key={k} label={PROFILE_LABELS[k] || humanize(k)}>
                {show(personal[k])}
              </Field>
            ))}

            {Object.keys(documents).map((type) => (
              <Field key={type} label={humanize(type).toUpperCase()}>
                {documents[type].length > 0 ? documents[type].join(", ") : NA}
              </Field>
            ))}

            <Field label="Alternate Phones" wide>
              {phones.length > 0 ? <Chips items={phones} /> : NA}
            </Field>
            <Field label="Emails" wide>
              {emails.length > 0 ? <Chips items={emails} /> : NA}
            </Field>

            {Object.keys(others).map((k) => (
              <Field key={k} label={humanize(k)} wide>
                {others[k].map((line, i) => (
                  <div key={i}>{line}</div>
                ))}
              </Field>
            ))}
          </div>

          <div className="enach-addr-wrap">
            <div className="enach-label">Addresses</div>
            {addresses.length > 0 ? (
              <ul className="enach-addr">
                {addresses.map((a, i) => (
                  <li key={i}>
                    {a.address}
                    {(a.pincode || a.date_of_reporting) && (
                      <span className="enach-addr-meta">
                        {" "}
                        ({a.pincode ? `PIN ${a.pincode}` : ""}
                        {a.pincode && a.date_of_reporting ? ", " : ""}
                        {a.date_of_reporting
                          ? `reported ${a.date_of_reporting}`
                          : ""}
                        )
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="enach-value">{NA}</div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// const CamEnachDetailsPage = ({ leadId }) => {
  const CamEnachDetailsPage = ({ leadId, loanApplicationId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [enachDetails, setEnachDetails] = useState([]);
  const [profile, setProfile] = useState(null);

  // per-card AllCloud state: { [index]: { sending, sent, message, ok } }
  const [allcloud, setAllcloud] = useState({});

  useEffect(() => {
    if (!leadId) return;

    const getEnachDetails = async () => {
      try {
        setLoading(true);

        const response = await fetch(
          `${config.API_BASE_URL}get_enach_upi_mandate_details`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: encryptData({ lead_id: leadId }),
          }
        );

        const result = await response.json();

        if (result.success) {
          setEnachDetails(Array.isArray(result.data) ? result.data : []);
          setProfile(result.profile || null);
          setError("");
        } else {
          setEnachDetails([]);
          setProfile(null);
          setError(result.message);
        }
      } catch (e) {
        setEnachDetails([]);
        setProfile(null);
        setError("Something went wrong while fetching eNach details.");
      } finally {
        setLoading(false);
      }
    };

    getEnachDetails();
  }, [leadId]);

  // "Coming soon" toast (real API call is kept below as sendToAllCloudApi)
  const navigate = useNavigate();

const sendToAllCloud = (item) => {
  if (!item.registration_id) {
    toast.error("Registration ID not found for this eNach entry.");
    return;
  }
  const encodedLeadId = btoa(String(leadId));
  const encodedLoanAppId = btoa(String(loanApplicationId || leadId));
  const encodedEnachId = btoa(String(item.registration_id));

 // navigate(`/dashboard/send-to-allcloud/${encodedLeadId}/${encodedLoanAppId}/${encodedEnachId}`);

  const url = `/dashboard/send-to-allcloud/${encodedLeadId}/${encodedLoanAppId}/${encodedEnachId}`;

   window.open(url, '_blank');
};

  // eslint-disable-next-line no-unused-vars
  const sendToAllCloudApi = async (item, index) => {
    setAllcloud((p) => ({ ...p, [index]: { sending: true } }));

    try {
      const response = await fetch(
        `${config.API_BASE_URL}send_enach_to_allcloud`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: encryptData({
            lead_id: leadId,
            registration_id: item.registration_id || "",
          }),
        }
      );

      const result = await response.json();

      setAllcloud((p) => ({
        ...p,
        [index]: {
          sending: false,
          sent: !!result.success,
          ok: !!result.success,
          message:
            result.message ||
            (result.success ? "Sent to AllCloud." : "Could not send to AllCloud."),
        },
      }));
    } catch (e) {
      setAllcloud((p) => ({
        ...p,
        [index]: {
          sending: false,
          sent: false,
          ok: false,
          message: "Something went wrong while sending to AllCloud.",
        },
      }));
    }
  };

  if (loading) {
    return <div className="text-center p-3">Loading...</div>;
  }

  return (
    <div className="row">
      <style>{`
        .enach-card {
          background: #fff;
          border: 1px solid ${COLORS.border};
          border-left: 4px solid ${COLORS.border};
          border-radius: 8px;
          margin-bottom: 12px;
          overflow: hidden;
        }
        .enach-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 8px;
          padding: 8px 14px;
          background: ${COLORS.tint};
          border-bottom: 1px solid ${COLORS.border};
        }
        .enach-title {
          margin: 0;
          font-size: 14.5px;
          font-weight: 600;
          color: ${COLORS.text};
        }
        .enach-actions {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 8px;
        }
        .enach-badge {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 2px 10px 2px 6px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 600;
          line-height: 18px;
          color: #fff;
        }
        .enach-badge-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: rgba(255,255,255,.28);
          font-size: 9px;
          line-height: 1;
        }
        .enach-body {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          padding: 4px 0;
        }
        .enach-field { padding: 6px 14px; }
        .enach-field-wide { grid-column: span 2; }
        .enach-field-full { grid-column: 1 / -1; }
        .enach-label {
          font-size: 11.5px;
          color: ${COLORS.label};
          line-height: 1.3;
        }
        .enach-value {
          font-size: 13px;
          font-weight: 500;
          line-height: 1.4;
          color: ${COLORS.text};
          word-break: break-word;
        }
        .enach-handles { display: flex; flex-wrap: wrap; gap: 4px; }
        .enach-chip {
          padding: 0 8px;
          border-radius: 4px;
          background: ${COLORS.tint};
          border: 1px solid ${COLORS.border};
          font-size: 12px;
          font-weight: 500;
          line-height: 20px;
        }
        .enach-addr-wrap {
          padding: 6px 14px 10px;
          border-top: 1px solid #eef4f6;
        }
        .enach-addr {
          margin: 2px 0 0;
          padding-left: 16px;
          font-size: 12.5px;
          font-weight: 500;
          line-height: 1.5;
          color: ${COLORS.text};
        }
        .enach-addr-meta { color: ${COLORS.label}; font-weight: 400; }
        .enach-reason {
          padding: 6px 14px;
          font-size: 12.5px;
          font-weight: 500;
          border-top: 1px solid #eef4f6;
        }
        .enach-btn {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          border: 0;
          border-radius: 6px;
          padding: 4px 12px;
          font-size: 12.5px;
          font-weight: 500;
          line-height: 18px;
          color: #fff;
          background: ${COLORS.info};
          cursor: pointer;
          transition: filter .15s ease;
        }
        .enach-btn:hover:not(:disabled) { filter: brightness(.93); }
        .enach-btn:focus-visible { outline: 2px solid rgba(13,202,240,.5); outline-offset: 2px; }
        .enach-btn:disabled { opacity: .75; cursor: not-allowed; }
        .enach-btn-done { background: ${COLORS.success}; }
        .enach-toggle {
          border: 1px solid ${COLORS.border};
          background: #fff;
          border-radius: 6px;
          padding: 2px 10px;
          font-size: 12px;
          color: ${COLORS.label};
          cursor: pointer;
        }
        .enach-toggle:hover { border-color: ${COLORS.info}; color: ${COLORS.text}; }
        .enach-msg { font-size: 12px; font-weight: 500; }
        .enach-created {
          display: inline-flex;
          align-items: center;
          padding: 2px 10px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 600;
          line-height: 18px;
          color: ${COLORS.success};
          background: #e8f5ee;
          border: 1px solid #b7dfc8;
        }
        @media (max-width: 991px) {
          .enach-body { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
        @media (max-width: 575px) {
          .enach-body { grid-template-columns: 1fr; }
          .enach-field-wide { grid-column: span 1; }
        }
      `}</style>

      <div className="col-md-12">
        {error && (
          <div className="text-danger text-center mb-3">{error}</div>
        )}

        {profile && <ProfileCard profile={profile} />}

        {enachDetails.map((item, index) => {
          const state = getMandateState(item);
          const meta = STATE_META[state];
          const ac = allcloud[index] || {};
          const fields = buildFields(item, state);

          return (
            <div
              key={item.registration_id || index}
              className="enach-card"
              style={{ borderLeftColor: meta.color }}
            >
              <div className="enach-head">
                <h5 className="enach-title">eNach Details {index + 1}</h5>

                <div className="enach-actions">
                  {item.allcloud_customer && (
                    <span className="enach-created">✓ Customer Created</span>
                  )}
                  {item.allcloud_lead && (
                    <span className="enach-created">✓ Lead Created</span>
                  )}
                  {item.allcloud_loan && (
                    <span className="enach-created">✓ Loan Created</span>
                  )}

                  {ac.message && (
                    <span
                      className="enach-msg"
                      style={{ color: ac.ok ? COLORS.success : COLORS.danger }}
                    >
                      {ac.message}
                    </span>
                  )}

                  {state === "success" && (
                    <button
                      type="button"
                      className={`enach-btn ${ac.sent ? "enach-btn-done" : ""}`}
                      disabled={ac.sending || ac.sent}
                      onClick={() => sendToAllCloud(item)}
                    >
                      {ac.sending
                        ? "Sending..."
                        : ac.sent
                        ? "✓ Sent to AllCloud"
                        : "Send to AllCloud"}
                    </button>
                  )}

                  <span
                    className="enach-badge"
                    style={{ background: meta.color }}
                    title={item.status || ""}
                  >
                    <span className="enach-badge-icon">{meta.icon}</span>
                    {meta.label}
                  </span>
                </div>
              </div>

              <div className="enach-body">
                {fields.map((fld, i) => (
                  <Field key={`${fld.label}-${i}`} label={fld.label} wide={fld.wide}>
                    {fld.value}
                  </Field>
                ))}
              </div>

              {(state === "failed" || state === "aborted") &&
                (item.error_message || item.error_code) && (
                  <div
                    className="enach-reason"
                    style={{ color: meta.color, background: `${meta.color}12` }}
                  >
                    <strong>Reason:</strong>{" "}
                    {item.error_code ? `${item.error_code} - ` : ""}
                    {item.error_message}
                  </div>
                )}
            </div>
          );
        })}

        {!loading && enachDetails.length === 0 && (
          <div className="text-center py-4">No eNach Details Found.</div>
        )}
      </div>
    </div>
  );
};

export default CamEnachDetailsPage;